/**
 * Quantity Breaks Picker (v7 - persistent instant update)
 *
 * Key fix over v6: After Sense's renderProductInfo replaces the price
 * block (which overwrites our instant update), we re-apply our update
 * by listening to the variant-change pubsub event.
 */
(function () {
  'use strict';
 
  class QuantityBreaksPicker extends HTMLElement {
    constructor() {
      super();
      this.mode = this.dataset.mode;
      this.formId = this.dataset.formId;
      this.sectionId = this.dataset.sectionId;
      this.packIndex = parseInt(this.dataset.packOptionIndex, 10) || 0;
      this._isUpdatingFromSelf = false;
      this._lastSelectedVariantId = null;
    }
 
    connectedCallback() {
      this.form = document.getElementById(this.formId);
      this.variantSelects = document.querySelector(
        'variant-selects[data-section="' + this.sectionId + '"]'
      );
 
      this._bindRadios();
      this._installHiddenMirror();
 
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsubVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event || !event.data) return;
          if (event.data.sectionId !== this.sectionId) return;
 
          // If this variant change came from our own QB click, the section
          // has just been re-rendered — our instant update may have been
          // overwritten. Re-apply it for stability.
          if (this._isUpdatingFromSelf && this._lastSelectedVariantId) {
            // Re-apply instant update after Sense's render completes
            this._instantPriceUpdate(this._lastSelectedVariantId, true);
            return;
          }
 
          // Came from external source (Color/Size click) — refresh QB picker
          this._refreshFromHTML(event.data.html);
        });
      }
    }
 
    disconnectedCallback() {
      if (this._unsubVariantChange) this._unsubVariantChange();
    }
 
    _bindRadios() {
      this.querySelectorAll('.qb-option__input').forEach((input) => {
        if (input._qbBound) return;
        input._qbBound = true;
        input.addEventListener('change', (e) => this._handleChange(e));
      });
    }
 
    _installHiddenMirror() {
      if (this.mode !== 'variant' || this.packIndex === 0) return;
      if (!this.variantSelects) return;
 
      let mirror = this.variantSelects.querySelector(
        'fieldset[data-qb-mirror]'
      );
      if (!mirror) {
        mirror = document.createElement('fieldset');
        mirror.setAttribute('data-qb-mirror', 'true');
        mirror.style.cssText = 'border:0;padding:0;margin:0;position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);';
        this.variantSelects.appendChild(mirror);
      }
 
      this._populateMirror(mirror);
    }
 
    _populateMirror(mirror) {
      const variants = this._getVariantData();
      if (!variants.length) return;
 
      const checkedQbInput = this.querySelector('.qb-option__input:checked');
      const selectedVariantId = checkedQbInput ? checkedQbInput.dataset.variantId : null;
 
      let selectedPackValue = null;
      if (selectedVariantId) {
        const variant = variants.find((v) => String(v.id) === String(selectedVariantId));
        if (variant) {
          selectedPackValue = variant['option' + this.packIndex];
        }
      }
 
      const seen = new Set();
      const packValues = [];
      variants.forEach((v) => {
        const val = v['option' + this.packIndex];
        if (val && !seen.has(val)) {
          seen.add(val);
          packValues.push(val);
        }
      });
 
      mirror.innerHTML = '';
      packValues.forEach((val, i) => {
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'qb-mirror-' + this.sectionId;
        radio.value = val;
        radio.id = 'qb-mirror-' + this.sectionId + '-' + i;
        if (val === selectedPackValue) radio.checked = true;
        mirror.appendChild(radio);
      });
    }
 
    _handleChange(e) {
      const input = e.target;
      const variantId = input.dataset.variantId;
      const qty = parseInt(input.dataset.qty, 10) || 1;
 
      // Visual feedback
      const label = this.querySelector('label[for="' + input.id + '"]');
      if (label && label.animate) {
        label.animate(
          [
            { transform: 'scale(1)' },
            { transform: 'scale(0.985)' },
            { transform: 'scale(1)' },
          ],
          { duration: 180, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
        );
      }
 
      this._updateQuantity(qty);
 
      if (this.mode === 'variant' && variantId) {
        this._lastSelectedVariantId = variantId;
 
        // 1. INSTANT update — no network wait
        this._instantPriceUpdate(variantId, false);
 
        // 2. Trigger Sense flow in background (URL, full section refresh)
        this._isUpdatingFromSelf = true;
        this._updateMirrorAndFireChange(variantId);
        // Reset flag after Sense's flow completes
        setTimeout(() => { this._isUpdatingFromSelf = false; }, 1500);
      }
 
      this.dispatchEvent(
        new CustomEvent('qb:change', {
          bubbles: true,
          detail: { qty, variantId, price: input.dataset.price },
        })
      );
    }
 
    /**
     * Update price block and badges instantly from variant JSON.
     * @param {string} variantId
     * @param {boolean} isReapply - true if called after Sense's render
     */
    _instantPriceUpdate(variantId, isReapply) {
      const variants = this._getVariantData();
      const variant = variants.find((v) => String(v.id) === String(variantId));
      if (!variant) return;
 
      const priceContainer = document.getElementById('price-' + this.sectionId);
      if (!priceContainer) return;
 
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
 
      // Update sale price (the actual current price shown)
      const saleEl = priceContainer.querySelector('.price-item--sale.price-item--last, .price-item--last');
      if (saleEl) saleEl.textContent = this._formatMoney(variant.price);
 
      // Update regular price in .price__regular section
      const regularEl = priceContainer.querySelector('.price__regular .price-item--regular');
      if (regularEl) regularEl.textContent = this._formatMoney(variant.price);
 
      // Update strikethrough compare-at price in .price__sale section
      const compareEl = priceContainer.querySelector('.price__sale s.price-item--regular');
      if (compareEl && onSale) {
        compareEl.textContent = this._formatMoney(variant.compare_at_price);
      }
 
      // Toggle classes for sale/sold-out state
      const priceWrapper = priceContainer.querySelector('.price');
      if (priceWrapper) {
        priceWrapper.classList.toggle('price--on-sale', !!onSale);
        priceWrapper.classList.toggle('price--sold-out', !variant.available);
      }
 
      // Update Save badge (your prepaid discount badge)
      const badge = document.getElementById('save-amount-badge');
      if (badge) {
        if (onSale) {
          const savings = variant.compare_at_price - variant.price;
          const formatted = (savings / 100).toLocaleString('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });
          badge.textContent = 'Save Rs. ' + formatted;
          badge.style.display = 'inline-flex';
        } else {
          badge.style.display = 'none';
        }
      }
 
      // Skip URL / form input updates on re-apply (Sense already did them)
      if (isReapply) return;
 
      // Update URL
      try {
        const productUrl = this.variantSelects && this.variantSelects.dataset.url;
        if (productUrl && window.history && window.history.replaceState) {
          window.history.replaceState({}, '', productUrl + '?variant=' + variantId);
        }
      } catch (err) { /* noop */ }
 
      // Update form variant input
      if (this.form) {
        const vinput = this.form.querySelector('input[name="id"]');
        if (vinput) vinput.value = variantId;
      }
    }
 
    /**
     * Format cents as "Rs. 1,499.00" matching Shopify Indian rupee format.
     */
    _formatMoney(cents) {
      if (typeof cents !== 'number') cents = parseInt(cents, 10);
      if (isNaN(cents)) return '';
      const rupees = cents / 100;
      const formatted = rupees.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return 'Rs. ' + formatted;
    }
 
    _updateQuantity(qty) {
      if (!this.form) return;
      const qtyInput = this.form.querySelector('input[name="quantity"]');
      if (!qtyInput) return;
      qtyInput.value = qty;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
 
    _updateMirrorAndFireChange(variantId) {
      const variants = this._getVariantData();
      const variant = variants.find((v) => String(v.id) === String(variantId));
      if (!variant) return;
 
      const targetPackValue = variant['option' + this.packIndex];
      if (!targetPackValue) return;
 
      const mirror = this.variantSelects && this.variantSelects.querySelector('fieldset[data-qb-mirror]');
      if (!mirror) return;
 
      const radios = mirror.querySelectorAll('input[type="radio"]');
      let target = null;
      radios.forEach((r) => {
        if (r.value === targetPackValue) {
          r.checked = true;
          target = r;
        } else {
          r.checked = false;
        }
      });
 
      if (!target) return;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
 
    _refreshFromHTML(html) {
      if (!html) return;
      const newPicker = html.querySelector(
        'quantity-breaks-picker[data-section-id="' + this.sectionId + '"]'
      );
      if (!newPicker) return;
      const newOptions = newPicker.querySelector('.qb-picker__options');
      const myOptions = this.querySelector('.qb-picker__options');
      if (!newOptions || !myOptions) return;
 
      myOptions.innerHTML = newOptions.innerHTML;
      this._bindRadios();
 
      const mirror = this.variantSelects && this.variantSelects.querySelector('fieldset[data-qb-mirror]');
      if (mirror) this._populateMirror(mirror);
    }
 
    _getVariantData() {
      if (this._variantDataCache) return this._variantDataCache;
      if (!this.variantSelects) return [];
      const script = this.variantSelects.querySelector('script[type="application/json"]');
      if (!script) return [];
      try {
        this._variantDataCache = JSON.parse(script.textContent);
      } catch (e) {
        this._variantDataCache = [];
      }
      return this._variantDataCache;
    }
  }
 
  if (!customElements.get('quantity-breaks-picker')) {
    customElements.define('quantity-breaks-picker', QuantityBreaksPicker);
  }
})();